import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation} from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../services/axios';
import alertService from '../services/alerts';
import '../styles/EditarAlumnoPage.css';

function EditarAlumnoPage() {
  const emptyData = {
    id_alumno: '',
    id_prospecto: '',
    nombre_completo: '',
    fecha_inscripcion: '',
    curso_actual: '',
    estado_actividad: '',
    numero_matricula: '',
    observaciones: '',
    razon_baja: '',
  };


  const [formData, setFormData] = useState(emptyData);
  const [initialData, setInitialData] = useState(emptyData);
  const [formErrors, setFormErrors] = useState({});
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState('');
  const [autoFillError, setAutoFillError] = useState('');
  const [isFormChanged, setIsFormChanged] = useState(false); 
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const estadoActividadOptions = ['activo', 'baja'];
  const [isFilterOpen, setIsFilterOpen] = useState(false);  
  const location = useLocation(); 
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1025);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1025;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    if (window.innerWidth >= 1025) {
      setSidebarOpen(true);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    const fetchCursos = async () => {
      try {
        const response = await api.get('/cursos');
        setCourses(response.data);
      } catch (err) {
        setError('Error al cargar cursos');
      }
    };
    fetchCursos();
  }, []);

  useEffect(() => {
    const getFormattedDate = () => {
      const today = new Date();
      today.setMinutes(today.getMinutes() - today.getTimezoneOffset()); 
      return today.toISOString().slice(0, 10);
    };
  
    if (isEditing) {
      const fetchAlumno = async () => {
        try {
          const res = await api.get(`/alumnos/${id}`);
          const a = res.data;
          setFormData({
            ...a,
            nombre_completo: a.nombre_completo || 'N/A',
            fecha_inscripcion: a.fecha_inscripcion.slice(0, 10),
          });
          setInitialData({
            ...a,
            nombre_completo: a.nombre_completo || 'N/A',
            fecha_inscripcion: a.fecha_inscripcion.slice(0, 10),
          });
        } catch {
          setError('Error al obtener datos del alumno');
        }
      };
      fetchAlumno();
    } else {
      setFormData({ ...emptyData, fecha_inscripcion: getFormattedDate() });
    }
  }, [isEditing, id]);
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };
      setIsFormChanged(JSON.stringify(updatedData) !== JSON.stringify(initialData)); 
      return updatedData;
    });
  };


  const handleLoadProspecto = async () => {
    setAutoFillError('');
    if (!formData.id_prospecto.trim()) {
      setAutoFillError('Ingresa un ID válido');
      return;
    }
    try {
      // Verificar que el prospecto existe
      await api.get(`/prospectos/${formData.id_prospecto}`);
      
      // Verificar si ya está registrado como alumno
      const alumnoExistente = await api.get(`/alumnos`);
      const yaInscrito = alumnoExistente.data.some(al => al.id_prospecto === parseInt(formData.id_prospecto));

      if (yaInscrito) {
        setAutoFillError('Este prospecto ya está registrado como alumno.');
      } else {
        setAutoFillError(''); // Limpiar error si todo está bien
      }
    } catch {
      setAutoFillError('Prospecto no encontrado');
    }
  };

  const validateForm = () => {
    const errors = {};
    const hoy = new Date().toISOString().slice(0,10);
  
    if (!formData.id_prospecto && !isEditing) errors.id_prospecto = 'ID requerido';
    if (!formData.fecha_inscripcion) {
      errors.fecha_inscripcion = 'Fecha requerida';
    } else if (formData.fecha_inscripcion > hoy) {
      errors.fecha_inscripcion = 'No puedes seleccionar una fecha futura';
    }
    if (!formData.curso_actual) errors.curso_actual = 'Curso requerido';
    if (!formData.estado_actividad) errors.estado_actividad = 'Estado requerido';
    if (!formData.numero_matricula && !isEditing) errors.numero_matricula = 'Matrícula requerida';
    return errors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    const confirmed = await alertService.confirm(
      isEditing ? 'Guardar Cambios' : 'Crear Alumno',
      isEditing ? '¿Estás seguro de guardar los cambios?' : '¿Deseas ingresar al alumno?',
      {
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar'
      }
    );
    if (!confirmed.isConfirmed) return;

    try {
      if (isEditing) {
        await api.put(`/alumnos/${id}`, {
          curso_actual: formData.curso_actual,
          estado_actividad: formData.estado_actividad,
          fecha_inscripcion: formData.fecha_inscripcion,
          observaciones: formData.observaciones,
          razon_baja: formData.razon_baja,
        });
        await alertService.success(
          'Alumno Actualizado',
          'Los cambios han sido guardados exitosamente.'
        );
      } else {
        const dataToSend = {
          id_prospecto: formData.id_prospecto,
          curso_actual: formData.curso_actual,
          estado_actividad: formData.estado_actividad,
          fecha_inscripcion: formData.fecha_inscripcion,
          numero_matricula: formData.numero_matricula,
          observaciones: formData.observaciones,
          razon_baja: formData.razon_baja,
        };
        console.log('=== DEBUG MATRÍCULA ===');
        console.log('Datos a enviar:', dataToSend);
        console.log('formData.numero_matricula:', formData.numero_matricula);
        console.log('Tipo de numero_matricula:', typeof formData.numero_matricula);
        console.log('¿Está vacío?', !formData.numero_matricula);
        console.log('¿Es string vacío?', formData.numero_matricula === '');
        console.log('¿Tiene espacios?', formData.numero_matricula && formData.numero_matricula.trim() === '');
        console.log('Token:', localStorage.getItem('token') ? 'Presente' : 'Ausente');
        console.log('========================');
        await api.post('/alumnos', dataToSend);
        await alertService.success(
          'Alumno Creado',
          'El alumno ha sido creado exitosamente.'
        );
      }
      navigate('/alumnos');
    } catch (error) {
      console.error('Error al guardar:', error);
      let errorMessage = 'Error al guardar datos';
      
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      
      await alertService.error('Error', errorMessage);
    }
  };

   
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isFormChanged) {
        const message = "Tienes cambios sin guardar. ¿Estás seguro de salir?";
        event.returnValue = message; 
        return message; 
      }
    };
  
    const handleHistoryPopState = async (event) => {
      if (isFormChanged) {
        event.preventDefault();
        const userConfirmed = await alertService.confirm(
          "Cambios Sin Guardar",
          "Tienes cambios sin guardar. ¿Estás seguro de salir?",
          {
            confirmButtonText: 'Sí, Salir',
            cancelButtonText: 'Cancelar'
          }
        );
        if (userConfirmed.isConfirmed) {
          navigate("/alumnos");
        } else {
          window.history.pushState(null, null, window.location.href); 
        }
      } else {
        navigate("/alumnos");
      }
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handleHistoryPopState);
  
    window.history.pushState(null, null, window.location.href); 
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleHistoryPopState);
    };
  }, [isFormChanged, navigate]);
  

  
  const handleBackButtonClick = async () => {
    if (isFormChanged) {
      const confirmed = await alertService.confirm(
        "Cambios Sin Guardar",
        "Tienes cambios sin guardar. ¿Estás seguro de salir?",
        {
          confirmButtonText: 'Sí, Salir',
          cancelButtonText: 'Cancelar'
        }
      );
      if (!confirmed.isConfirmed) return; 
    }
    navigate('/alumnos'); 
  };


  return (
    <div className="dashboard-container">
      {(!isMobile || (isMobile && sidebarOpen)) && (
        <Sidebar
          onLogoClick={() => {
            if (isMobile) setSidebarOpen(false);
          }}
          onLinkClick={handleCloseSidebar}
        />
      )}
      <div className={`main-content ${isFilterOpen ? 'panel-open' : ''}`} onClick={handleCloseSidebar}>
        <div className="dashboard-title-card">
          {isMobile && (
            <button 
              className="menu-toggle"
              onClick={toggleSidebar}
              aria-label="Abrir menú"
            >
              <i className="fas fa-bars"></i>
            </button>
          )}
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              {isEditing ? 'Editar Alumno' : 'Crear Alumno'}
            </h1>
            <span className="dashboard-title-sub">
              {isEditing ? 'Modificar información del alumno' : 'Registrar nuevo alumno'}
            </span>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="futuristic-form-container">
          <div className="form-header">
            <h2 className="form-title">
              <i className="fas fa-graduation-cap"></i>
              Información del Alumno
            </h2>
            <button className="back-button" onClick={handleBackButtonClick}>
              <i className="fas fa-arrow-left"></i>
              Regresar
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="two-column-form">
              <div className="form-column">
                {isEditing && (
                  <div className="form-group">
                    <label>ID Alumno</label>
                    <input
                      type="text"
                      name="id_alumno"
                      value={formData.id_alumno}
                      disabled
                      className="form-input"
                    />
                  </div>
                )}

                {isEditing && (
                  <div className="form-group">
                    <label>Nombre Alumno</label>
                    <input
                      type="text"
                      name="nombre_completo"
                      value={formData.nombre_completo}
                      disabled
                      className="form-input"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>ID Prospecto</label>
                  <input
                    type="number"
                    name="id_prospecto"
                    value={formData.id_prospecto}
                    onChange={handleChange}
                    disabled={isEditing}
                    placeholder="Ingresa el ID del prospecto"
                    className={`form-input ${formErrors.id_prospecto ? 'error' : ''}`}
                  />
                  {formErrors.id_prospecto && (
                    <span className="error-message">{formErrors.id_prospecto}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Fecha Inscripción</label>
                  <input
                    type="date"
                    name="fecha_inscripcion"
                    value={formData.fecha_inscripcion}
                    onChange={handleChange}
                    className={`form-input ${formErrors.fecha_inscripcion ? 'error' : ''}`}
                  />
                  {formErrors.fecha_inscripcion && (
                    <span className="error-message">{formErrors.fecha_inscripcion}</span>
                  )}
                </div>
              </div>

              <div className="form-column">
                <div className="form-group">
                  <label>Curso Actual</label>
                  <select
                    name="curso_actual"
                    value={formData.curso_actual}
                    onChange={handleChange}
                    className={`select-input ${formErrors.curso_actual ? 'error' : ''}`}
                  >
                    <option value="">Selecciona un curso</option>
                    {courses.map(c => (
                      <option key={c.id} value={c.nombre_curso}>
                        {c.nombre_curso}
                      </option>
                    ))}
                  </select>
                  {formErrors.curso_actual && (
                    <span className="error-message">{formErrors.curso_actual}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Estado Actividad</label>
                  <select
                    name="estado_actividad"
                    value={formData.estado_actividad}
                    onChange={handleChange}
                    className={`select-input ${formErrors.estado_actividad ? 'error' : ''}`}
                  >
                    <option value="">Selecciona un estado</option>
                    {estadoActividadOptions.map(o => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                  {formErrors.estado_actividad && (
                    <span className="error-message">{formErrors.estado_actividad}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Número de Matrícula</label>
                  <input
                    type="text"
                    name="numero_matricula"
                    value={formData.numero_matricula}
                    onChange={handleChange}
                    placeholder="Ingresa el número de matrícula"
                    className={`form-input ${formErrors.numero_matricula ? 'error' : ''}`}
                    disabled={isEditing}
                  />
                  {formErrors.numero_matricula && (
                    <span className="error-message">{formErrors.numero_matricula}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Observaciones</label>
                  <textarea
                    name="observaciones"
                    value={formData.observaciones}
                    onChange={handleChange}
                    placeholder="Ingresa observaciones adicionales..."
                    className={`form-input ${formErrors.observaciones ? 'error' : ''}`}
                    rows="4"
                  />
                  {formErrors.observaciones && (
                    <span className="error-message">{formErrors.observaciones}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Razón de Baja</label>
                  <select
                    name="razon_baja"
                    value={formData.razon_baja}
                    onChange={handleChange}
                    className={`select-input ${formErrors.razon_baja ? 'error' : ''}`}
                  >
                    <option value="">Selecciona una razón de baja (si aplica)</option>
                    <option value="Problemas económicos">Problemas económicos</option>
                    <option value="Problemas de horario">Problemas de horario</option>
                    <option value="Problemas familiares">Problemas familiares</option>
                    <option value="Razones personales">Razones personales</option>
                    <option value="Problemas de conexión">Problemas de conexión</option>
                    <option value="Falta de adaptación en línea">Falta de adaptación en línea</option>
                    <option value="Inconforme con el curso">Inconforme con el curso</option>
                    <option value="Bajo rendimiento">Bajo rendimiento</option>
                    <option value="Diferencias con el docente">Diferencias con el docente</option>
                  </select>
                  {formErrors.razon_baja && (
                    <span className="error-message">{formErrors.razon_baja}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="submit-button-container">
              <button 
                type="submit" 
                className="submit-button"
                disabled={!isFormChanged}
              >
                <i className="fas fa-save"></i>
                {isEditing ? 'Guardar Cambios' : 'Crear Alumno'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {isMobile && sidebarOpen && (
        <div className="overlay" onClick={handleCloseSidebar}></div>
      )}
    </div>
  );
}

export default EditarAlumnoPage;
